import { useState, useEffect } from 'react';
import { HelpCircle, Send, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

interface Props {
    questions: string[];
    onSubmit: (answers: string[]) => void;
    isSubmitting: boolean;
    error?: string | null;
}

export default function ClarifyingQuestions({ questions, onSubmit, isSubmitting, error }: Props) {
    const [answers, setAnswers] = useState<string[]>(new Array(questions.length).fill(''));

    const handleAnswerChange = (index: number, value: string) => {
        const newAnswers = [...answers];
        newAnswers[index] = value;
        setAnswers(newAnswers);
    };

    const getValidAnswersCount = () => {
        return answers.filter(a => a.trim().length > 0).length;
    };

    const handleSubmit = () => {
        onSubmit(answers);
    };

    // Recuperar rascunho do localStorage ao montar
    useEffect(() => {
        const saved = localStorage.getItem('clarifying_answers_draft');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length === questions.length) {
                    setAnswers(parsed);
                }
            } catch {
                // Ignora erros de parse
            }
        }
    }, [questions.length]);

    // Salvar rascunho em localStorage ao alterar respostas
    useEffect(() => {
        localStorage.setItem('clarifying_answers_draft', JSON.stringify(answers));
    }, [answers]);

    // Limpar rascunho ao submeter
    useEffect(() => {
        if (isSubmitting) {
            localStorage.removeItem('clarifying_answers_draft');
        }
    }, [isSubmitting]);

    return (
        <div className="space-y-6">
            <div className="flex items-start space-x-3 p-4 bg-accent-50 dark:bg-accent-900/20 rounded-xl border border-accent-200 dark:border-accent-800">
                <HelpCircle className="h-5 w-5 text-accent-500 mt-0.5 flex-shrink-0" />
                <div>
                    <p className="text-sm font-medium text-accent-700 dark:text-accent-300">
                        Perguntas Esclarecedoras
                    </p>
                    <p className="text-sm text-accent-600 dark:text-accent-400 mt-1">
                        Responda as perguntas abaixo para que a IA gere um parecer mais personalizado.
                        Todas as respostas são opcionais.
                    </p>
                </div>
            </div>

            {error && (
                <div className="p-4 bg-error-500/10 border border-error-500/30 text-error-600 dark:text-error-400 rounded-xl flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <div className="space-y-4">
                {questions.map((question, index) => {
                    const hasAnswer = answers[index] && answers[index].trim().length > 0;

                    return (
                        <div
                            key={index}
                            className={`p-4 rounded-xl border transition-all duration-200 ${hasAnswer
                                ? 'bg-success-50 dark:bg-success-900/10 border-success-200 dark:border-success-800'
                                : 'bg-navy-50 dark:bg-navy-800/50 border-navy-200 dark:border-navy-700'
                                }`}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <label className="text-sm font-medium text-navy-700 dark:text-navy-200 flex items-center">
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-100 dark:bg-accent-900 text-accent-600 dark:text-accent-400 text-xs font-bold mr-2">
                                        {index + 1}
                                    </span>
                                    {question}
                                </label>
                                {hasAnswer && (
                                    <CheckCircle2 className="h-4 w-4 text-success-500 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            <textarea
                                value={answers[index]}
                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                className="w-full p-3 rounded-lg border border-navy-300 dark:border-navy-600 bg-white dark:bg-navy-900 text-navy-900 dark:text-navy-100 placeholder-navy-400 dark:placeholder-navy-500 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30 transition-all duration-200 resize-none"
                                rows={2}
                                placeholder="Opcional - deixe em branco se não aplicável"
                                disabled={isSubmitting}
                            />
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-navy-200 dark:border-navy-700">
                <div className="text-sm text-navy-500 dark:text-navy-400">
                    {getValidAnswersCount()} de {questions.length} respondidas
                </div>
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="inline-flex items-center px-5 py-2.5 btn-accent rounded-lg disabled:bg-navy-300 disabled:text-navy-500 dark:disabled:bg-navy-700 dark:disabled:text-navy-500"
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processando...
                        </>
                    ) : (
                        <>
                            <Send className="h-4 w-4 mr-2" />
                            Continuar
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
